import {ReactNode, useEffect, useState} from "react";
import {Box, Button, LinearProgress} from "@mui/material";
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridSortModel,
	GridValidRowModel,
	GridRowParams,
	GridSelectionModel,
	MuiEvent,
	useGridApiRef,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import {FileDownloadOutlined} from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

/**
 * Props for AdminTable component
 */
export interface AdminTableProps<T extends GridValidRowModel = any> {
	/** Data to display */
	rows: T[];
	
	/** Total count for pagination */
	totalCount: number;
	
	/** Column definitions */
	columns: GridColDef<T>[];
	
	/** Loading state */
	loading?: boolean;
	
	/** Current page (0-indexed) */
	page?: number;
	
	/** Page size */
	pageSize?: number;
	
	/** Available page size options */
	pageSizeOptions?: number[];
	
	/** Sort model */
	sortModel?: GridSortModel;
	
	/** Filter model */
	filterModel?: GridFilterModel;
	
	/** Initial column visibility model */
	initialColumnVisibility?: Record<string, boolean>;
	
	/** Row click handler */
	onRowClick?: (row: T) => void;
	
	/** Page change handler */
	onPageChange?: (page: number) => void;
	
	/** Page size change handler */
	onPageSizeChange?: (pageSize: number) => void;
	
	/** Sort change handler */
	onSortModelChange?: (model: GridSortModel) => void;
	
	/** Filter change handler */
	onFilterModelChange?: (model: GridFilterModel) => void;
	
	/** Toolbar text (e.g., "VIEWING STATS FROM THE PAST WEEK") */
	toolbarText?: string;
	
	/** Export button handler */
	onExport?: () => void;
	
	/** Export button disabled state */
	exportDisabled?: boolean;
	
	/** Export button loading state */
	exportLoading?: boolean;
	
	/** Export button text */
	exportButtonText?: string;
	
	/** Enable advanced filtering */
	enableAdvancedFilters?: boolean;
	
	/** Enable column filter */
	enableColumnFilter?: boolean;
	
	/** Checkbox selection */
	checkboxSelection?: boolean;
	
	/** Controlled selection model */
	selectionModel?: GridSelectionModel;
	
	/** Selection change handler */
	onSelectionModelChange?: (model: GridSelectionModel) => void;
	
	/** Get row ID function */
	getRowId?: (row: T) => string | number;
	
	/** Auto height */
	autoHeight?: boolean;
	
	/** Table height */
	height?: number | string;
	
	/** Additional toolbar content */
	toolbarSlot?: ReactNode;
	
	/** Disable column resize */
	disableColumnResize?: boolean;
	
	/** Hide footer */
	hideFooter?: boolean;
	
	/** Keep selected rows that are no longer in the current data set (useful for server-side pagination) */
	keepNonExistentRowsSelected?: boolean;
}

/**
 * Custom toolbar for AdminTable
 * Layout matches production: COLUMNS | FILTERS | EXPORT (inline), VIEWING STATS FROM text aligned far right.
 */
function AdminTableToolbar({
	toolbarText,
	onExport,
	exportDisabled,
	exportLoading,
	exportButtonText = "Export",
	enableAdvancedFilters = true,
	enableColumnFilter = true,
	toolbarSlot
}: {
	toolbarText?: string;
	onExport?: () => void;
	exportDisabled?: boolean;
	exportLoading?: boolean;
	exportButtonText?: string;
	enableAdvancedFilters?: boolean;
	enableColumnFilter?: boolean;
	toolbarSlot?: ReactNode;
}) {
	return (
		<GridToolbarContainer sx={{pt: 0, pb: 1, px: 2, justifyContent: "space-between", alignItems: "center"}}>
			<Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
				{enableColumnFilter && <GridToolbarColumnsButton {...({} as any)} />}
				{enableAdvancedFilters && <GridToolbarFilterButton {...({} as any)} />}
				{onExport && (
					<Button
						startIcon={<FileDownloadOutlined />}
						variant="text"
						size="small"
						onClick={onExport}
						disabled={exportDisabled || exportLoading}
					>
						{exportLoading ? "Exporting..." : exportButtonText}
					</Button>
				)}
				{toolbarSlot}
			</Box>
			{toolbarText && (
				<Box
					sx={{
						fontWeight: 400,
						fontSize: "16px",
						letterSpacing: "0.15px",
						opacity: 0.8,
						color: "rgb(255, 255, 255)"
					}}
				>
					{toolbarText}
				</Box>
			)}
		</GridToolbarContainer>
	);
}

/**
 * AdminTable - A React Query compatible data grid with advanced features
 * 
 * Features:
 * - Column visibility management (persisted to localStorage)
 * - Column resizing and reordering (persisted to localStorage)
 * - Advanced filtering
 * - Server-side pagination, sorting, and filtering
 * - Export functionality
 * - Toolbar with filter description
 */
export function AdminTable<T extends GridValidRowModel = any>({
	rows,
	totalCount,
	columns,
	loading = false,
	page = 0,
	pageSize = 10,
	pageSizeOptions = [10, 25, 50, 100, 200],
	sortModel = [],
	filterModel,
	initialColumnVisibility = {},
	onRowClick,
	onPageChange,
	onPageSizeChange,
	onSortModelChange,
	onFilterModelChange,
	toolbarText,
	onExport,
	exportDisabled = false,
	exportLoading = false,
	exportButtonText,
	enableAdvancedFilters = true,
	enableColumnFilter = true,
	checkboxSelection = false,
	selectionModel,
	onSelectionModelChange,
	getRowId,
	autoHeight = false,
	height = 600,
	toolbarSlot,
	disableColumnResize = false,
	hideFooter = false,
	keepNonExistentRowsSelected = false
}: AdminTableProps<T>) {
	const apiRef = useGridApiRef();
	
	// Generate a unique key for this table based on column fields
	const tableKey = `admin-table-${columns.map(c => c.field).join("-")}`;
	
	// Column visibility state (persisted to localStorage)
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
		const saved = localStorage.getItem(`${tableKey}-visibility`);
		return saved ? JSON.parse(saved) : initialColumnVisibility;
	});
	
	// Track if spacing/order has been modified
	const [spacingModified, setSpacingModified] = useState(false);
	
	// Handle column visibility changes
	const handleColumnVisibilityChange = (newModel: Record<string, boolean>) => {
		setColumnVisibilityModel(newModel);
		setSpacingModified(true);
		localStorage.setItem(`${tableKey}-visibility`, JSON.stringify(newModel));
	};
	
	// Subscribe to column order and width changes
	useEffect(() => {
		const unsubscribeOrder = apiRef.current.subscribeEvent("columnOrderChange", () => {
			const orderedFields = apiRef.current.getAllColumns().map(col => col.field);
			localStorage.setItem(`${tableKey}-order`, JSON.stringify(orderedFields));
			setSpacingModified(true);
		});
		
		const unsubscribeWidth = apiRef.current.subscribeEvent("columnWidthChange", () => {
			const columnWidths: Record<string, number> = {};
			apiRef.current.getAllColumns().forEach(col => {
				if (col.field) {
					columnWidths[col.field] = col.computedWidth || col.width || 100;
				}
			});
			setSpacingModified(true);
			localStorage.setItem(`${tableKey}-widths`, JSON.stringify(columnWidths));
		});
		
		// Check if we have saved state
		if (
			localStorage.getItem(`${tableKey}-order`) ||
			localStorage.getItem(`${tableKey}-widths`) ||
			localStorage.getItem(`${tableKey}-visibility`)
		) {
			setSpacingModified(true);
		}
		
		return () => {
			unsubscribeOrder();
			unsubscribeWidth();
		};
	}, [apiRef, tableKey]);
	
	// Reset spacing and order
	const resetSpacingAndOrder = () => {
		localStorage.removeItem(`${tableKey}-order`);
		localStorage.removeItem(`${tableKey}-widths`);
		localStorage.removeItem(`${tableKey}-visibility`);
		setSpacingModified(false);
		setColumnVisibilityModel(initialColumnVisibility);
	};
	
	// Apply saved column order and widths
	const storedColumnOrder: string[] = JSON.parse(localStorage.getItem(`${tableKey}-order`) || "[]");
	const storedColumnWidths: Record<string, number> = JSON.parse(localStorage.getItem(`${tableKey}-widths`) || "{}");
	
	const orderedColumns = storedColumnOrder.length
		? storedColumnOrder.map((field: string) => columns.find(col => col.field === field)).filter(Boolean) as GridColDef<T>[]
		: columns;
	
	const finalColumns = orderedColumns.map((col) => ({
		...col,
		width: storedColumnWidths[col.field] || col.width,
		flex: storedColumnWidths[col.field] ? undefined : col.flex
	}));
	
	// Handle row click
	const handleRowClick = (params: GridRowParams<T>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
		// Check if click is on a button or interactive element
		const target = event.target as HTMLElement;
		if (target.closest("button") || target.closest(".MuiCheckbox-root")) {
			return;
		}
		onRowClick?.(params.row);
	};
	
	return (
		<Box
			sx={{
				width: "100%",
				height: autoHeight ? "auto" : height,
				border: "1px solid rgba(255, 255, 255, 0.12)",
				borderRadius: "8px",
				overflow: "hidden"
			}}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DataGridPro
					apiRef={apiRef}
					rows={rows}
					columns={finalColumns}
					loading={loading}
					rowCount={totalCount}
					// Pagination: enable full UI (Rows per page, range, arrows) per MUI TablePagination
					pagination
					paginationMode="server"
					page={page}
					pageSize={pageSize}
					onPageChange={(newPage) => onPageChange?.(newPage)}
					onPageSizeChange={(newPageSize) => onPageSizeChange?.(newPageSize)}
					rowsPerPageOptions={pageSizeOptions}
					// Sorting
					sortingMode="server"
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					// Filtering
					filterMode="server"
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					// Column visibility
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityChange}
					// Selection
					checkboxSelection={checkboxSelection}
					selectionModel={selectionModel}
					onSelectionModelChange={onSelectionModelChange}
					keepNonExistentRowsSelected={keepNonExistentRowsSelected}
					disableSelectionOnClick
					// Row interaction
					onRowClick={handleRowClick}
					getRowId={getRowId}
					// Layout
					autoHeight={autoHeight}
					disableColumnResize={disableColumnResize}
					hideFooter={hideFooter}
					// Toolbar
					components={{
						Toolbar: () => (
							<AdminTableToolbar
								toolbarText={toolbarText}
								onExport={onExport}
								exportDisabled={exportDisabled}
								exportLoading={exportLoading}
								exportButtonText={exportButtonText}
								enableAdvancedFilters={enableAdvancedFilters}
								enableColumnFilter={enableColumnFilter}
								toolbarSlot={
									spacingModified ? (
										<Button size="small" onClick={resetSpacingAndOrder}>
											Reset Layout
										</Button>
									) : toolbarSlot
								}
							/>
						),
						LoadingOverlay: LinearProgress
					}}
					// Styling: container border + single divider under header, thin row dividers (match production)
					sx={{
						border: "none",
						"& .MuiDataGrid-row": {
							cursor: onRowClick ? "pointer" : "default"
						},
						"& .MuiDataGrid-row .MuiDataGrid-cell": {
							py: 1
						},
						"& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle": {
							textTransform: "none"
						},
						"& .MuiDataGrid-columnHeaders": {
							borderBottom: "1px solid rgba(255, 255, 255, 0.12)"
						},
						"& .MuiDataGrid-columnHeader": {
							borderBottom: "none"
						},
						"& .MuiDataGrid-cell": {
							borderTop: "none",
							borderBottom: "1px solid rgba(255, 255, 255, 0.12)"
						},
						"& .MuiDataGrid-footerContainer": {
							minHeight: "53px"
						},
						"& .MuiDataGrid-footerContainer .MuiTablePagination-root": {
							minHeight: "53px"
						},
						"& .MuiDataGrid-footerContainer .MuiTablePagination-toolbar": {
							minHeight: "53px"
						}
					}}
				/>
			</LocalizationProvider>
		</Box>
	);
}
